import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import AlertIcon from "@redotech/redo-icons/alert-triangle.svg";
import BlueYonderIcon from "@redotech/redo-icons/blueyonder-logo.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { WebhookEventType } from "@redotech/redo-model/blue-yonder-integration";
import { COMMON } from "@redotech/redo-model/localization/language";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  ChannelConnectionState,
  SettingsCard,
} from "@redotech/redo-web/arbiter-components/settings-card/settings-card";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, listInput } from "@redotech/ui/form";
import { memo, useEffect, useMemo, useState } from "react";
import { AiToolParams } from "../integrations";
import * as integrationsCss from "../marketing/marketing-integrations.module.css";
interface BlueYonderLocation {
  blueyonderLocationId: string;
  locationName: string;
}

interface BlueYonderIntegration {
  retailerCompanyId: string;
  webhookSubscriptionId: string;
  locations: BlueYonderLocation[];
  enabled?: boolean;
  testMode: boolean;
  processingEvent: WebhookEventType | null;
  radiusMiles: number;
  title: string | null;
  subtitle: string | null;
  locationsText: string | null;
}

export const ReturnsBlueYonderIntegration = memo(
  function ReturnsBlueYonderIntegration({ onSubmit }: AiToolParams) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [connectEditModalOpen, setConnectEditModalOpen] = useState(false);
    const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
    const canEditReturnsSettings = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );

    const [blueYonderIntegrationLoad, triggerBlueYonderIntegrationLoad] =
      useTriggerLoad(async () => {
        const integration = await rpcClient.getBlueYonderIntegration({});
        if (!integration.blueYonderIntegration) {
          return undefined;
        }
        const byIntegration: BlueYonderIntegration = {
          ...integration.blueYonderIntegration,
          radiusMiles: integration.blueYonderIntegration?.radiusMiles ?? 100,
          title: integration.blueYonderIntegration?.title ?? null,
          subtitle: integration.blueYonderIntegration.subtitle ?? null,
          locationsText:
            integration.blueYonderIntegration.locationsText ?? null,
        };
        return byIntegration;
      });

    useEffect(() => {
      triggerBlueYonderIntegrationLoad();
    }, [triggerBlueYonderIntegrationLoad]);

    // Get integration details
    const blueYonderIntegration = blueYonderIntegrationLoad.value;

    // Check if we have an enabled integration returned from the API
    const hasEnabledIntegration =
      !!blueYonderIntegration && blueYonderIntegration.enabled === true;

    // Check if we have a disabled integration
    const hasDisabledIntegration =
      !!blueYonderIntegration && blueYonderIntegration.enabled === false;

    const handlePrimaryButtonClick = useHandler(() => {
      if (hasDisabledIntegration) {
        setReconnectModalOpen(true);
      } else {
        setConnectEditModalOpen(true);
      }
    });

    const handleManageButtonClick = useHandler(() => {
      setConnectEditModalOpen(true);
    });

    const getConnectionState = useMemo(() => {
      if (blueYonderIntegrationLoad.pending)
        return ChannelConnectionState.LOADING;
      if (!hasEnabledIntegration && !hasDisabledIntegration)
        return ChannelConnectionState.NOT_CONNECTED;
      if (hasDisabledIntegration) return ChannelConnectionState.NOT_CONNECTED;
      return ChannelConnectionState.CONNECTED;
    }, [
      blueYonderIntegrationLoad.pending,
      hasEnabledIntegration,
      hasDisabledIntegration,
    ]);

    const getButtonSection = useMemo(() => {
      if (blueYonderIntegrationLoad.pending || !canEditReturnsSettings)
        return undefined;

      if (!hasEnabledIntegration) {
        return (
          <RedoButton
            className={integrationsCss.integrationButton}
            hierarchy="primary"
            onClick={handlePrimaryButtonClick}
            size="md"
            text={hasDisabledIntegration ? "Reconnect" : "Connect"}
          />
        );
      }

      return (
        <Flex dir="row" gap="sm">
          <RedoButton
            hierarchy="tertiary"
            onClick={() => setDisconnectModalOpen(true)}
            size="lg"
            text="Disconnect"
          />
          <RedoButton
            className={integrationsCss.integrationButton}
            hierarchy="secondary"
            onClick={handleManageButtonClick}
            size="md"
            text="Manage"
          />
        </Flex>
      );
    }, [
      blueYonderIntegrationLoad.pending,
      canEditReturnsSettings,
      hasEnabledIntegration,
      hasDisabledIntegration,
      handlePrimaryButtonClick,
      handleManageButtonClick,
    ]);

    return (
      <>
        <SettingsCard
          buttonSection={getButtonSection}
          cardWidth="third"
          connectionState={getConnectionState}
          description="Connect FedEx Easy Returns to Redo"
          Logo={BlueYonderIcon}
          name="FedEx Easy Returns"
        />

        <ConnectEditBlueYonderModal
          existingIntegration={blueYonderIntegration}
          modalOpen={connectEditModalOpen}
          onSubmit={() => {
            onSubmit?.({
              aiSuccessMessage:
                "Successfully connected FedEx Easy Returns, you should use get-integration and show the merchant what the current state is.",
            });
            triggerBlueYonderIntegrationLoad();
          }}
          setModalOpen={setConnectEditModalOpen}
        />

        <DisconnectBlueYonderModal
          modalOpen={disconnectModalOpen}
          onSubmit={() => {
            onSubmit?.({
              aiSuccessMessage:
                "The merchant has disconnected FedEx Easy Returns, you should tell the merchant that they can reconnect at any time but currently no customers will be able to use boxless returns.",
            });
            triggerBlueYonderIntegrationLoad();
          }}
          setModalOpen={setDisconnectModalOpen}
        />

        <DisconnectBlueYonderModal
          isReconnect
          modalOpen={reconnectModalOpen}
          onSubmit={() => {
            onSubmit?.({
              aiSuccessMessage:
                "The merchant has connected FedEx Easy Returns, you should tell the merchant that users can now use boxless returns if their shipping settings are configured to use FedEx Easy Returns.",
            });
            triggerBlueYonderIntegrationLoad();
          }}
          setModalOpen={setReconnectModalOpen}
        />
      </>
    );
  },
);

export const blueYonderLocationForm = groupInput({
  blueyonderLocationId: input<string>(),
  locationName: input<string>(),
});

export const blueYonderIntegrationForm = groupInput({
  retailerCompanyId: input<string>(),
  locations: listInput(
    () => blueYonderLocationForm,
    () => ({ blueyonderLocationId: "", locationName: "" }),
    (_, index) => index,
  ),
  processingEvent: input<WebhookEventType | null>(),
  testMode: input<boolean>(),
  radiusMiles: input<number>(),
  title: input<string | null>(),
  subtitle: input<string | null>(),
  locationsText: input<string | null>(),
});

export const blueYonderIntegrationDefault = {
  retailerCompanyId: "",
  locations: [],
  processingEvent: WebhookEventType.DROPPED_OFF,
  testMode: false,
  radiusMiles: 100,
  title: null,
  subtitle: null,
  locationsText: null,
};

let enabledState: boolean | null | undefined = null;

export const ConnectEditBlueYonderModal = memo(
  function ConnectEditBlueYonderModal({
    modalOpen,
    setModalOpen,
    onSubmit,
    existingIntegration,
  }: {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    onSubmit: () => void;
    existingIntegration?: BlueYonderIntegration | null;
  }) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    const [newLocation, setNewLocation] = useState<BlueYonderLocation>({
      blueyonderLocationId: "",
      locationName: "",
    });
    const [isAddingLocation, setIsAddingLocation] = useState(false);

    // Create the form with initial values
    const form = useInput(
      blueYonderIntegrationForm,
      existingIntegration || blueYonderIntegrationDefault,
    );

    // Reset add location state when modal opens/closes
    useEffect(() => {
      setIsAddingLocation(false);
    }, [modalOpen]);

    useEffect(() => {
      if (existingIntegration && existingIntegration.enabled !== enabledState) {
        enabledState = existingIntegration.enabled;
        form.inputs.retailerCompanyId.setValue(
          existingIntegration.retailerCompanyId,
        );
        form.inputs.locations.setValue(existingIntegration.locations);
        form.inputs.processingEvent.setValue(
          existingIntegration.processingEvent ?? WebhookEventType.DROPPED_OFF,
        );
        form.inputs.testMode.setValue(existingIntegration.testMode ?? false);
        form.inputs.radiusMiles.setValue(
          existingIntegration.radiusMiles ?? 100,
        );
        form.inputs.title.setValue(
          existingIntegration.title ?? blueYonderIntegrationDefault.title,
        );
        form.inputs.subtitle.setValue(
          existingIntegration.subtitle ?? blueYonderIntegrationDefault.subtitle,
        );
        form.inputs.locationsText.setValue(
          existingIntegration.locationsText ??
            blueYonderIntegrationDefault.locationsText,
        );
      }
    }, [
      existingIntegration,
      form.inputs.locations,
      form.inputs.retailerCompanyId,
      form.inputs.processingEvent,
      form.inputs.testMode,
      form.inputs.radiusMiles,
      form.inputs.title,
      form.inputs.subtitle,
      form.inputs.locationsText,
    ]);

    // Determine if form has meaningful changes
    const hasChanges = form.changed;

    const [submitLoad, doSubmitLoad] = useTriggerLoad(async () => {
      // Set/update the integration
      await rpcClient.setBlueYonderIntegration({
        retailerCompanyId: form.value.retailerCompanyId || "",
        locations: form.value.locations.map((loc) => ({
          blueyonderLocationId: loc.blueyonderLocationId,
          locationName: loc.locationName,
        })),
        processingEvent: form.value.processingEvent ?? undefined,
        testMode: form.value.testMode,
        radiusMiles: form.value.radiusMiles ?? 100,
        title: form.value.title ?? undefined,
        subtitle: form.value.subtitle ?? undefined,
        locationsText: form.value.locationsText ?? undefined,
      });

      setModalOpen(false);
      onSubmit();
    });

    const isFormValid = useMemo(
      () => !!form.value.retailerCompanyId,
      [form.value.retailerCompanyId],
    );

    const addLocation = useHandler(() => {
      if (newLocation.blueyonderLocationId && newLocation.locationName) {
        const newLocations = [
          ...form.value.locations,
          {
            blueyonderLocationId: newLocation.blueyonderLocationId,
            locationName: newLocation.locationName,
          },
        ];

        // Update form input directly
        form.inputs.locations.setValue(newLocations);

        setNewLocation({ blueyonderLocationId: "", locationName: "" });
        setIsAddingLocation(false);
      }
    });

    const removeLocation = useHandler((index: number) => {
      const updatedLocations = [...form.value.locations];
      updatedLocations.splice(index, 1);

      // Update form input directly
      form.inputs.locations.setValue(updatedLocations);
    });

    const handleModalClose = useHandler(() => setModalOpen(false));

    const handleAddLocationStart = useHandler(() => setIsAddingLocation(true));

    const handleAddLocationCancel = useHandler(() =>
      setIsAddingLocation(false),
    );

    const primaryButton = useMemo(() => {
      return hasChanges
        ? {
            text: "Save",
            onClick: doSubmitLoad,
            disabled: submitLoad.pending || !isFormValid || isAddingLocation,
          }
        : undefined;
    }, [
      hasChanges,
      doSubmitLoad,
      submitLoad.pending,
      isFormValid,
      isAddingLocation,
    ]);

    const secondaryButton = useMemo(
      () => ({
        text: hasChanges ? "Cancel" : "Close",
        onClick: handleModalClose,
        disabled: submitLoad.pending,
      }),
      [hasChanges, handleModalClose, submitLoad.pending],
    );

    const handleRetailerCompanyIdChange = useHandler((value: string) => {
      form.inputs.retailerCompanyId.setValue(value);
    });

    const handleTitleChange = useHandler((value: string | null) => {
      form.inputs.title.setValue(value);
    });

    const handleSubtitleChange = useHandler((value: string | null) => {
      form.inputs.subtitle.setValue(value);
    });

    const handleLocationsTextChange = useHandler((value: string | null) => {
      form.inputs.locationsText.setValue(value);
    });

    const handleNewLocationNameChange = useHandler((value: string) => {
      setNewLocation({ ...newLocation, locationName: value });
    });

    const handleNewLocationIdChange = useHandler((value: string) => {
      setNewLocation({ ...newLocation, blueyonderLocationId: value });
    });

    const handleProcessingEventChange = useHandler(
      (value: WebhookEventType | null) => {
        form.inputs.processingEvent.setValue(value);
      },
    );

    const handleTestModeChange = useHandler((checked: boolean) => {
      form.inputs.testMode.setValue(checked);
    });

    const handleRadiusChange = useHandler((value: number) => {
      form.inputs.radiusMiles.setValue(value);
    });

    // Create webhook event options for the dropdown
    const webhookEventOptions = useMemo(() => {
      const options: TextRedoListItem<WebhookEventType | null>[] = [
        {
          type: "text",
          text: "None (Manual Processing)",
          id: "None",
          value: null,
        },
        {
          type: "text",
          text: "Dropped Off",
          id: WebhookEventType.DROPPED_OFF,
          value: WebhookEventType.DROPPED_OFF,
        },
        {
          type: "text",
          text: "Dispatched from BlueYonder",
          id: WebhookEventType.DESPATCHED_FROM_PUDO,
          value: WebhookEventType.DESPATCHED_FROM_PUDO,
        },
        {
          type: "text",
          text: "Arrived at Destination",
          id: WebhookEventType.ARRIVED_AT_DESTINATION,
          value: WebhookEventType.ARRIVED_AT_DESTINATION,
        },
      ];
      return options;
    }, []);

    return (
      <RedoModal
        isOpen={modalOpen}
        modalSize="md"
        onModalCloseRequested={handleModalClose}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        title={
          existingIntegration
            ? "Edit FedEx Easy Returns"
            : "Connect FedEx Easy Returns"
        }
      >
        <Flex dir="column" gap="4xl">
          <LabeledInput
            description="This ID is provided by FedEx Easy Returns during the onboarding process. Contact your FedEx Easy Returns representative if you don't have this information."
            label="Retailer Company ID"
          >
            <RedoTextInput
              setValue={handleRetailerCompanyIdChange}
              size="sm"
              value={form.value.retailerCompanyId}
            />
          </LabeledInput>

          <LabeledInput label="Title">
            <RedoTextInput
              placeholder={COMMON.ShipmentMethodName_BLUE_YONDER_TITLE}
              setValue={(value: string) =>
                handleTitleChange(value ? value : null)
              }
              size="sm"
              value={form.value.title ?? ""}
            />
          </LabeledInput>

          <LabeledInput label="Subtitle">
            <RedoTextInput
              placeholder={COMMON.ShipmentMethodName_BLUE_YONDER_SUBTEXT}
              setValue={(value: string) =>
                handleSubtitleChange(value ? value : null)
              }
              size="sm"
              value={form.value.subtitle ?? ""}
            />
          </LabeledInput>

          <LabeledInput label="Location Text">
            <RedoTextInput
              placeholder={COMMON.ShipmentMethodName_BLUE_YONDER_LOCATIONS_TEXT}
              setValue={(value: string) =>
                handleLocationsTextChange(value ? value : null)
              }
              size="sm"
              value={form.value.locationsText ?? ""}
            />
          </LabeledInput>

          <LabeledInput
            description="Select which webhook event should trigger automatic processing of return items. Choose 'None' for manual processing only."
            label="Processing Event"
          >
            <RedoSingleSelectDropdownInput
              options={webhookEventOptions}
              optionSelected={(option) =>
                option.selected
                  ? handleProcessingEventChange(option.item.value)
                  : handleProcessingEventChange(null)
              }
              placeholder="Select processing event..."
              selectedItem={webhookEventOptions.find(
                (option) => form.inputs.processingEvent.value === option.value,
              )}
              size="sm"
            />
          </LabeledInput>

          <LabeledInput
            description="Enable test mode to use sandbox credentials for testing. When disabled, production credentials will be used."
            label="Test Mode"
          >
            <RedoToggle
              setValue={handleTestModeChange}
              size="sm"
              value={form.value.testMode}
            />
          </LabeledInput>

          <RedoIncrementDecrement
            description="Maximum distance in miles that customers can be from a FedEx Easy Returns location to see the drop-off option."
            label="Radius (miles)"
            max={500}
            min={1}
            setValue={(value) => handleRadiusChange(value)}
            size="regular"
            value={form.value.radiusMiles ?? 100}
          />

          <Flex dir="column" gap="md">
            <Text fontWeight="semibold">Locations</Text>

            {form.value.locations.length === 0 ? (
              <Text>
                No locations configured yet. Click "Add Location" to add your
                first location.
              </Text>
            ) : (
              <Flex dir="column" gap="md">
                {form.value.locations.map((location, index) => (
                  <Flex
                    align="center"
                    dir="row"
                    justify="space-between"
                    key={index}
                    style={{
                      border: "1px solid var(--redo-gray-200)",
                      borderRadius: "var(--redo-border-radius)",
                    }}
                  >
                    <Flex dir="column" gap="sm">
                      <Flex dir="row" gap="md">
                        <Text fontWeight="semibold">
                          {location.locationName}
                        </Text>
                        <Text color="dim">
                          (ID: {location.blueyonderLocationId})
                        </Text>
                      </Flex>
                    </Flex>
                    <RedoButton
                      hierarchy="tertiary"
                      onClick={() => removeLocation(index)}
                      size="xs"
                      text="Remove"
                    />
                  </Flex>
                ))}
              </Flex>
            )}

            {!isAddingLocation && (
              <Flex justify="flex-start" mt="md">
                <RedoButton
                  hierarchy="secondary"
                  onClick={handleAddLocationStart}
                  size="xs"
                  text="Add Location"
                />
              </Flex>
            )}
          </Flex>

          {isAddingLocation && (
            <Flex
              align="center"
              dir="row"
              gap="md"
              justify="space-between"
              mt="md"
              style={{
                border: "1px solid var(--redo-gray-200)",
                borderRadius: "var(--redo-border-radius)",
              }}
            >
              <Flex dir="row" gap="md" grow={1} style={{ paddingLeft: "8px" }}>
                <RedoTextInput
                  infoTooltip="This is an internal name only"
                  placeholder="Location Name"
                  setValue={handleNewLocationNameChange}
                  size="md"
                  value={newLocation.locationName}
                />
                <RedoTextInput
                  infoTooltip="This is the location ID that you will receive from FedEx Easy Returns"
                  placeholder="FedEx Easy Returns Location ID"
                  setValue={handleNewLocationIdChange}
                  size="md"
                  value={newLocation.blueyonderLocationId}
                />
              </Flex>
              <Flex
                dir="row"
                gap="md"
                shrink={0}
                style={{ paddingRight: "8px" }}
              >
                <RedoButton
                  hierarchy="tertiary"
                  onClick={handleAddLocationCancel}
                  size="xs"
                  text="Cancel"
                />
                <RedoButton
                  disabled={
                    !newLocation.blueyonderLocationId ||
                    !newLocation.locationName
                  }
                  hierarchy="secondary"
                  onClick={addLocation}
                  size="xs"
                  text="Add"
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </RedoModal>
    );
  },
);

export const DisconnectBlueYonderModal = memo(
  function DisconnectBlueYonderModal({
    modalOpen,
    setModalOpen,
    onSubmit,
    isReconnect = false,
  }: {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    onSubmit: () => void;
    isReconnect?: boolean;
  }) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    const [actionLoad, doActionLoad] = useTriggerLoad(async () => {
      await rpcClient.disconnectBlueYonderIntegration({
        enabled: isReconnect, // true for reconnect, false for disconnect
      });
      setModalOpen(false);
      onSubmit();
    });

    const handleModalClose = useHandler(() => setModalOpen(false));

    const primaryButton = useMemo(
      () => ({
        text: isReconnect ? "Reconnect" : "Disconnect",
        onClick: doActionLoad,
        disabled: actionLoad.pending,
      }),
      [isReconnect, doActionLoad, actionLoad.pending],
    );

    const secondaryButton = useMemo(
      () => ({
        text: "Cancel",
        onClick: handleModalClose,
        disabled: actionLoad.pending,
      }),
      [handleModalClose, actionLoad.pending],
    );

    return (
      <RedoModal
        isOpen={modalOpen}
        modalSize="sm"
        onModalCloseRequested={handleModalClose}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        theme={isReconnect ? "info" : "warn"}
        title={
          isReconnect
            ? "Reconnect FedEx Easy Returns"
            : "Disconnect FedEx Easy Returns"
        }
        TitleIcon={AlertIcon}
      >
        <Text>
          {isReconnect
            ? "Are you sure you want to reconnect to FedEx Easy Returns? This will enable the integration with your existing settings."
            : "Are you sure you want to disconnect FedEx Easy Returns? This will disable the integration but preserve your settings in case you want to reconnect later."}
        </Text>
      </RedoModal>
    );
  },
);
